import * as React from 'react';

import {
  SearchField,
  Button,
  Toggle,
  ToggleButtonGroup,
  cn,
} from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { Plus, X } from 'lucide-react';

import {
  $searchTerm,
  $selectedStatus,
  $selectedTriggerType,
  $remoteAutomations,
} from '../../_state/automationList.state';
import {
  AUTOMATION_STATUS_OPTIONS,
  TRIGGER_TYPE_OPTIONS,
} from '../../constants';
import { useTranslations } from '../../i18n/useTranslations';
import { AutomationStatus, AutomationTriggerType } from '../../types';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

type AutomationFilterBarProps = {
  className?: string;
  onCreateNew?: () => void;
};

export const AutomationFilterBar: React.FC<AutomationFilterBarProps> = ({
  className,
  onCreateNew,
}) => {
  const t = useTranslations();
  const [searchTerm, setSearchTerm] = useDebouncedAtom($searchTerm, 300);
  const [selectedStatus, setSelectedStatus] = useAtom($selectedStatus);
  const [selectedTriggerType, setSelectedTriggerType] =
    useAtom($selectedTriggerType);

  const { data } = useAtomValue($remoteAutomations);
  const automations = React.useMemo(
    () => data?.automations || [],
    [data?.automations],
  );

  const statusCounts = React.useMemo(() => {
    const counts: Record<AutomationStatus, number> = {
      Draft: 0,
      Active: 0,
      Paused: 0,
      Archived: 0,
    };

    automations.forEach((automation) => {
      counts[automation.status]++;
    });

    return counts;
  }, [automations]);

  const totalCount = automations.length;

  const hasActiveFilters = Boolean(
    searchTerm?.trim() || selectedStatus || selectedTriggerType,
  );

  const handleClearAll = React.useCallback(() => {
    setSearchTerm(undefined);
    setSelectedStatus(undefined);
    setSelectedTriggerType(undefined);
  }, [setSearchTerm, setSelectedStatus, setSelectedTriggerType]);

  return (
    <div
      className={cn(
        '@AutomationFilterBar',
        'flex items-center gap-3 p-4',
        className,
      )}
    >
      <ToggleButtonGroup
        selectionMode="single"
        selectedKeys={selectedStatus ? [selectedStatus] : ['all']}
        onSelectionChange={(v) => {
          const value = Array.from(v.values())[0] as string;
          setSelectedStatus(
            value === 'all' ? undefined : (value as AutomationStatus),
          );
        }}
      >
        <Toggle id="all" badge={totalCount}>
          {t.list.all}
        </Toggle>
        {AUTOMATION_STATUS_OPTIONS.map((status) => (
          <Toggle
            key={status}
            id={status}
            badge={statusCounts[status]}
            isDisabled={statusCounts[status] === 0}
          >
            {t.status[status]}
          </Toggle>
        ))}
      </ToggleButtonGroup>

      <SearchField
        placeholder={t.list.search}
        aria-label={t.list.searchAriaLabel}
        value={searchTerm || ''}
        onChange={(value) => setSearchTerm(value || undefined)}
      />

      <ToggleButtonGroup
        selectionMode="single"
        selectedKeys={
          selectedTriggerType ? [selectedTriggerType] : ['all-triggers']
        }
        onSelectionChange={(v) => {
          const value = Array.from(v.values())[0] as string;
          setSelectedTriggerType(
            value === 'all-triggers'
              ? undefined
              : (value as AutomationTriggerType),
          );
        }}
      >
        <Toggle id="all-triggers">{t.list.allTriggers}</Toggle>
        {TRIGGER_TYPE_OPTIONS.map((triggerType) => (
          <Toggle key={triggerType} id={triggerType}>
            {t.triggerType[triggerType]}
          </Toggle>
        ))}
      </ToggleButtonGroup>

      {hasActiveFilters && (
        <Button
          variant="text"
          color="primary"
          icon={<X />}
          onPress={handleClearAll}
        >
          {t.list.clearFilters}
        </Button>
      )}

      <div className="flex-1" />

      {onCreateNew && (
        <Button
          variant="solid"
          color="primary"
          icon={<Plus className="size-4" />}
          onPress={onCreateNew}
        >
          {t.list.createNew}
        </Button>
      )}
    </div>
  );
};
